import {
  Box,
  Typography,
  TypographyProps,
  styled,
  useTheme,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import useImage from 'use-image';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { usePointDetections } from '../../services/data/pointDetections/hooks/usePointDetections';
import { useTranslation } from '../../services/translation/hooks/useTranslation';
import LoadingSpinner from '../commonUI/LoadingSpinner';
import { flexCenter } from '../commonUI/cssClasses';
import { DuckDBStatusEnum, selectDuckDBStatus } from '../duckDB/duckDBSlice';
import {
  ScanDataStatusEnum,
  selectCurrAIServiceVersionOverride,
  selectCurrScanID,
  selectScanDataStatus,
} from '../select/selectSlice';
import ImageStage from './ImageStage';
import {
  selectBackendColorCorrection,
  selectBackendIntendedColorCorrection,
  selectCameraSelection,
  selectFrontendColorCorrection,
  selectFrontendColorCorrectionResult,
  selectImageResolution,
  setBackendColorCorrection,
  setFrontendColorCollectionResult,
} from './imageSlice';
import { useFrontendColorCorrection } from './openCV/hooks/useFrontendColorCorrection';
import { useImageContext } from './providers/ImageProvider';
import { checkImageExists } from './utils/checkImageExists';
import {
  filterDetectionsByModelVersion,
  getDepthMapUrl,
  getImageUrl,
} from './utils/imageUtils';
enum ImageStatusEnum {
  Loading = 'loading',
  NoData = 'noData',
  Error = 'error',
  ReadyToDisplay = 'readyToDisplay',
}

export interface ImageScaleParams {
  x: number;
  y: number;
  scale: number;
}

const StyledMessage = styled(Typography)<TypographyProps>(({ theme }) => ({
  width: '100%',
  height: '100%',
  p: '1rem',
  textAlign: 'center',
  color: theme.colors.common.softWhite,
  backgroundColor: theme.colors.common.black,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

export type ImageURLType = {
  url: string;
  depthMapUrl: string;
} | null;

interface Props {
  widthParent: number;
  heightParent: number;
}

/**
 * High-Level Summary:
 * -------------------
 * The ImageController component manages how an image is displayed within the app.
 * It tracks the state of the image (like if it’s still loading, or ready),
 * and conditionally renders the final image or relevant status messages.
 * This helps separate logic for loading, error display, and complete image rendering.
 *
 * Major Props & Variables:
 * - imageStatus: Tracks if the image is ready or in another state.
 * - finalImage: The processed image to display.
 * - widthParent, heightParent: Dimensions passed down to the stage for proper scaling.
 * - detectionsFiltered: Filtered detection results (AI detection masks).
 */
const ImageController: React.FC<Props> = ({ widthParent, heightParent }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const scanId = useAppSelector(selectCurrScanID);

  const [imageStatus, setImageStatus] = useState<ImageStatusEnum>(
    ImageStatusEnum.NoData
  );
  const { currPoint, currPointLoading, currPointError } = useImageContext();
  const currScanID = useAppSelector(selectCurrScanID);
  const currAIServiceVersionOverride = useAppSelector(
    selectCurrAIServiceVersionOverride
  );
  const scanDataStatus = useAppSelector(selectScanDataStatus);
  const duckDBStatus = useAppSelector(selectDuckDBStatus);
  const frontendColorCorrection = useAppSelector(selectFrontendColorCorrection);
  const frontendColorCorrectionResult = useAppSelector(
    selectFrontendColorCorrectionResult
  );
  const imageResolution = useAppSelector(selectImageResolution);
  const cameraSelection = useAppSelector(selectCameraSelection);
  const backendColorCorrection = useAppSelector(selectBackendColorCorrection);
  const backendIntendedColorCorrection = useAppSelector(
    selectBackendIntendedColorCorrection
  );

  const { pointDetections, isPointDetectionsLoading } = usePointDetections(
    currPoint?.point_id || '',
    scanId || ''
  );

  // Build image URL's
  const imageUrls: ImageURLType = useMemo(() => {
    if (!currPoint) return null;
    const params = {
      format: 'jpg',
      width: imageResolution,
    };
    return {
      url: getImageUrl(
        currPoint,
        cameraSelection,
        backendColorCorrection,
        params
      ),
      depthMapUrl: getDepthMapUrl(currPoint.point_img_left_cc, params),
    };
  }, [currPoint, imageResolution, cameraSelection, backendColorCorrection]);

  // Fetch main image and color correct on the frontend
  const [image, imageFetchStatus] = useImage(imageUrls?.url || '', 'anonymous');
  const { finalImage } = useFrontendColorCorrection(
    true,
    image,
    [128, 256, 512, 1024]
  );

  // When new image URL's are detected, set image status to 'loading' and reset frontend color correction error states
  useEffect(() => {
    setImageStatus(ImageStatusEnum.Loading);

    dispatch(
      setFrontendColorCollectionResult({
        finalClipLimit: 0,
        error: false,
        isLoading: frontendColorCorrection ? true : false,
      })
    );
  }, [dispatch, imageUrls?.url, frontendColorCorrection]);

  // Set image status to loading if scanDataStatus, duckDB, or imageFetchStatus are loading
  useEffect(() => {
    if (
      scanDataStatus === ScanDataStatusEnum.loading ||
      duckDBStatus === DuckDBStatusEnum.updating ||
      imageFetchStatus === 'loading'
    ) {
      setImageStatus(ImageStatusEnum.Loading);
    }
  }, [scanDataStatus, duckDBStatus, imageFetchStatus]);

  // Update image status depending on various processing states such as scanDataStatus, duckDBStatus, etc...
  useEffect(() => {
    if (
      checkImageExists(finalImage) &&
      !currPointLoading &&
      !currPointError &&
      currScanID &&
      scanDataStatus === ScanDataStatusEnum.success &&
      duckDBStatus === DuckDBStatusEnum.dataLoaded &&
      !frontendColorCorrectionResult.isLoading &&
      imageFetchStatus === 'loaded'
    ) {
      setImageStatus(ImageStatusEnum.ReadyToDisplay);
    } else if (
      scanDataStatus === ScanDataStatusEnum.error ||
      imageFetchStatus === 'failed'
    ) {
      setImageStatus(ImageStatusEnum.Error);
    }
  }, [
    finalImage,
    currPointLoading,
    currPointError,
    currScanID,
    scanDataStatus,
    duckDBStatus,
    frontendColorCorrection,
    frontendColorCorrectionResult.isLoading,
    imageFetchStatus,
  ]);

  // Legacy Code Start --------------------------------------------------------------------------------
  // Restore intended backend color correction for each scan - This is legacy code that is likely not needed anymore since frontend color correction is now the default, but kept for now in case it is needed in the future
  useEffect(() => {
    dispatch(setBackendColorCorrection(backendIntendedColorCorrection));
  }, [currPoint?.scan_id, backendIntendedColorCorrection, dispatch]);

  // Switch to raw images if color-corrected image fails
  useEffect(() => {
    if (imageFetchStatus === 'failed' && backendColorCorrection) {
      console.log(
        t(
          'Color-corrected images are not available for this scan and/or camera selection'
        )
      );
      dispatch(setBackendColorCorrection(false));
    }
  }, [imageFetchStatus, backendColorCorrection, dispatch, t]);
  // Legacy Code End --------------------------------------------------------------------------------

  const detectionsFiltered = useMemo(
    () =>
      filterDetectionsByModelVersion(
        pointDetections,
        currAIServiceVersionOverride
      ),
    [pointDetections, currAIServiceVersionOverride]
  );

  // Display status messages and loading spinner
  let imageStatusDisplay: JSX.Element | null;
  if (!currScanID) {
    imageStatusDisplay = (
      <StyledMessage variant='h5'>
        {t('Please select a scan.', { ns: 'ranch-view' })}
      </StyledMessage>
    );
  } else if (imageStatus === ImageStatusEnum.Loading) {
    imageStatusDisplay = <LoadingSpinner />;
  } else if (
    imageStatus === ImageStatusEnum.NoData ||
    imageStatus === ImageStatusEnum.Error ||
    !checkImageExists(finalImage) ||
    !finalImage
  ) {
    imageStatusDisplay = (
      <StyledMessage variant='h5'>
        {t('No Data Found', { ns: 'ranch-view' })}
      </StyledMessage>
    );
  } else {
    imageStatusDisplay = null;
  }

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        position: 'relative',
        zIndex: 2,
        ...flexCenter,
        backgroundColor: theme.colors.common.black,
      }}
    >
      {imageStatusDisplay}
      <ImageStage
        isVisible={imageStatus === ImageStatusEnum.ReadyToDisplay}
        finalImage={finalImage}
        widthParent={widthParent}
        heightParent={heightParent}
        detectionsFiltered={detectionsFiltered}
        isDetectionsLoadingOrFetching={isPointDetectionsLoading}
        imageUrls={imageUrls}
      />
    </Box>
  );
};

export default ImageController;
